import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import type { PopupMethodParams } from "@/modules/billing/popup/PopupMethodParams.ts";
import type { SiteAmount } from "@/modules/currency/currencyTypes.ts";
import type { BillingAccountCard, InvoiceId, PaymentTarget } from "@warrenio/api-spec/spec.oats.gen";
import type { Ref } from "react";

export const PaymentResult = {
    /** Validation of form fields failed */
    VALIDATION_FAILED: "VALIDATION_FAILED",
    /** eg. modal was closed */
    CANCELLED: "CANCELLED",
    SUCCESS: "SUCCESS",
    WAIT_RESPONSE: "WAIT_RESPONSE",
    CLOSE_MANUALLY: "CLOSE_MANUALLY",
} as const;

export type PaymentResult = (typeof PaymentResult)[keyof typeof PaymentResult];

export type PayableInvoiceId = Exclude<InvoiceId, "current">;

export interface PaymentParams extends PopupMethodParams {
    account: EBillingAccount;
    currency: string;
    /**
     * Site-currency amount available for frontend processor setup.
     * For invoice payments the backend receives `target.invoice_id` and decides the charge amount.
     */
    setupAmount: SiteAmount;

    /** NB: Only available for creditcard-type methods */
    getCard: () => BillingAccountCard;
    target: PaymentTarget;
}

export function getPaymentTarget(params: PaymentParams): Pick<PaymentParams, "target"> {
    return { target: params.target };
}

/** Actions exposed by a "Payment" form */
export interface PaymentActions {
    /** @returns should a payment window be opened to display the progress and handle any redirects? */
    needsPopUp?: (params: PaymentParams) => boolean;

    makePayment(params: PaymentParams): Promise<PaymentResult>;
}

/** Properties for a "Payment" form */
export interface PaymentProps {
    isSubmitting: boolean;
    actionsRef: Ref<PaymentActions>;
    account: EBillingAccount;
    /** Site-currency amount to pay, without fees and VAT (top up amount or invoice amount before tax) */
    amount: SiteAmount;
}
